import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode, RefObject } from "react";
import { Auswahl, Eingabe, Knopf, Schalter } from "@kundeninfo/ui/me-design";
import type { EingabeProps, KnopfProps } from "@kundeninfo/ui/me-design";
import { zahlAus, mitEinheit } from "../layoutEntwurf.js";
import { knopfFokus, useInnererPlatzhalter } from "../../meHuellen.js";

// Die Übergänge für Fokus und Platzhalter stehen seit P4 in components/meHuellen.tsx (auch
// für Kundenakte und Listen); `knopfFokus` bleibt hier erreichbar.
export { knopfFokus };

/*
 * Die FELDER der Register (ME-Etappe 4, P3): Hüllen über `me-eingabe`, `me-auswahl`,
 * `me-schalter` und `me-knopf` statt eigener `<input>`/`<select>`/`<button>`. Was die
 * Elemente (Stand 511ef0f) nicht mitbringen, steht hier als Kundeninfo-Zutat — jede mit
 * Begründung:
 *
 *  - EINHEIT (mm/pt/%) neben dem Feld und der Knopf „Systemwert“: kein Element (Lagebild P3).
 *    Die Beschriftung steht deshalb als eigenes `<label for>` über der Zeile [Feld | Einheit |
 *    Systemwert] — so steht die Einheit auf Höhe des Feldes, nicht der Beschriftung; der Name
 *    kommt zusätzlich als `aria-label` an den Host (me-eingabe spiegelt ihn ins innere Feld).
 *  - PLATZHALTER = Systemwert: `me-eingabe` kennt kein `placeholder` (Befund an me-design,
 *    siehe Bericht P3). Übergang: `useInnererPlatzhalter` setzt ihn am inneren Feld.
 *  - VERLASSEN: die Hülle reicht kein `onBlur` durch; `useBeimVerlassen` hört am Host auf
 *    `blur` (das innere Feld meldet es über die Schattengrenze an den Host).
 *  - BEFUND AM FELD: Rahmen, `aria-invalid` und `aria-describedby` macht das Element über
 *    `fehler`; die Ansage als `role="alert"` steht zusätzlich unsichtbar im Light DOM
 *    (`FeldBefund`) — das Element selbst sagt einen neuen Fehler nicht an.
 *  - SPERRE im Zustand „Fremder Stand“: `fieldset disabled` erreicht die Felder im Shadow DOM
 *    nicht. Der Editor stellt sie deshalb über `PanelSperre` bereit; jedes Feld wird
 *    `gesperrt` (readOnly + aria-disabled, der Fokus bleibt), jeder Knopf `deaktiviert`.
 */

/** Die Sperre des Panels im Zustand „Fremder Stand“ (409) — gesetzt vom Editor. */
export const PanelSperre = createContext(false);

/** Ist das Panel gesperrt (Fremder Stand)? */
export function usePanelSperre(): boolean {
  return useContext(PanelSperre);
}

/** Die DOM-Kennung zu einem Entwurfspfad — `seitenraender.oben` → `lay-seitenraender-oben`. */
export function feldId(pfad: string): string {
  return `lay-${pfad.replace(/[^A-Za-z0-9]+/g, "-")}`;
}

/** Ruft `fn`, wenn der Fokus das Feld verlässt (`blur` am Host, über die Schattengrenze). */
function useBeimVerlassen(host: RefObject<HTMLElement | null>, fn: (() => void) | undefined): void {
  const neuester = useRef(fn);
  neuester.current = fn;
  useEffect(() => {
    const element = host.current;
    if (element === null) return undefined;
    const zuhoerer = (): void => { neuester.current?.(); };
    element.addEventListener("blur", zuhoerer);
    return () => element.removeEventListener("blur", zuhoerer);
  }, [host]);
}

/**
 * Die Ansage eines Befunds: unsichtbar, `role="alert"`, im Light DOM. Sichtbar steht der Text
 * im Element (`fehler`); hier wird er nur einmal angesagt, sobald er erscheint.
 */
export function FeldBefund(p: { id: string; text: string | undefined }): ReactElement | null {
  return p.text === undefined || p.text === "" ? null
    : <span id={p.id} className="lay__sr" role="alert">{p.text}</span>;
}

/** Mehrere Befunde eines Feldes zu einem Text für `fehler` (leer = keiner). */
export function befundText(...texte: (string | undefined)[]): string | undefined {
  const da = texte.filter((t): t is string => t !== undefined && t !== "");
  return da.length === 0 ? undefined : da.join(" ");
}

/**
 * Ein Eingabefeld (`me-eingabe`) der Register, kontrolliert über `wert`. `beschriftung`
 * zeigt das Element selbst; ohne sie trägt `name` den zugänglichen Namen (aria-label, für
 * Felder mit eigenem `<label for>` oder ohne sichtbare Beschriftung).
 */
export function LayEingabe(p: {
  id: string; wert: string; onText: (text: string) => void; onVerlassen?: (() => void) | undefined;
  name?: string | undefined; beschriftung?: string | undefined;
  platzhalter?: string | undefined; fehler?: string | undefined; hilfe?: string | undefined;
  max?: number | undefined; mehrzeilig?: boolean | undefined; gesperrt?: boolean | undefined;
  eigen?: boolean | undefined; schmal?: boolean | undefined; zahl?: boolean | undefined;
  className?: string | undefined;
}): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  const gesperrt = usePanelSperre() || p.gesperrt === true;
  useInnererPlatzhalter(host, p.platzhalter);
  useBeimVerlassen(host, p.onVerlassen);
  // `exactOptionalPropertyTypes`: nur gesetzte Angaben weiterreichen.
  const optional: Partial<EingabeProps> = {};
  if (p.name !== undefined) optional["aria-label"] = p.name;
  if (p.beschriftung !== undefined) optional.beschriftung = p.beschriftung;
  if (p.fehler !== undefined && p.fehler !== "") optional.fehler = p.fehler;
  if (p.hilfe !== undefined) optional.hilfe = p.hilfe;
  if (p.max !== undefined) optional.max = p.max;
  if (p.mehrzeilig === true) { optional.mehrzeilig = true; optional.zeilen = 3; }
  if (gesperrt) optional.gesperrt = true;
  if (p.zahl === true) optional.inputMode = "decimal";
  // Ohne eigene Beschriftung (eigenes `<label for>` oder nur aria-label) blendet
  // `lay__me--ohne-beschriftung` die leere Beschriftungszeile des Elements aus — sonst stünde
  // das Feld um deren Abstand tiefer als Einheit und Knopf daneben.
  const klassen = ["lay__me", p.beschriftung === undefined ? "lay__me--ohne-beschriftung" : "",
    p.eigen === true ? "lay__eigen" : "",
    p.schmal === true ? "lay__me--schmal" : "", p.className ?? ""].filter((k) => k !== "");
  return (
    <Eingabe ref={host} id={p.id} className={klassen.join(" ")} wert={p.wert} {...optional}
      onGeaendert={(d) => { if (!gesperrt) p.onText(d.wert); }} />
  );
}

/**
 * Ein Knopf der Register (`me-knopf`). `name` ist der zugängliche Name, wo der sichtbare Text
 * allein nicht reicht („Oberer Rand: Systemwert“, „Bezeichnung nach oben“): me-knopf reicht
 * kein aria-label an seinen inneren Knopf (Befund P3-2), der Name steht deshalb als
 * unsichtbarer Text im Slot; sichtbare Zeichen ohne Aussage (▲, „(an)“) sind `aria-hidden`.
 * Im Fremden Stand ist er deaktiviert.
 */
export function LayKnopf(p: {
  art?: KnopfProps["art"]; deaktiviert?: boolean | undefined; className?: string | undefined;
  /** Ausserhalb eines Panels (Meldungen, Werkzeugleiste) gilt die Panelsperre nicht. */
  ohneSperre?: boolean | undefined;
  onClick: () => void; children: ReactNode;
}): ReactElement {
  const sperre = usePanelSperre();
  const aus = (sperre && p.ohneSperre !== true) || p.deaktiviert === true;
  return (
    <Knopf ref={knopfFokus} art={p.art ?? "sekundaer"} deaktiviert={aus}
      {...(p.className === undefined ? {} : { className: p.className })}
      onClick={() => { if (!aus) p.onClick(); }}>{p.children}</Knopf>
  );
}

/** Der Knopf „Systemwert“ eines Feldes: Name „<Feld>: Systemwert“, sichtbar „Systemwert“
 *  (mit `zusatz`, z. B. „(an)“, nur fürs Auge). */
export function SystemwertKnopf(p: {
  label: string; zusatz?: string | undefined; onClick: () => void;
}): ReactElement {
  return (
    <LayKnopf art="leise" className="lay__systemwert" onClick={p.onClick}>
      <span className="lay__sr">{`${p.label}: `}</span>Systemwert
      {p.zusatz !== undefined && <span aria-hidden="true">{` ${p.zusatz}`}</span>}
    </LayKnopf>
  );
}

/**
 * Ein Zahlenfeld mit Einheit (mm, pt, %). Der SYSTEMWERT steht als Platzhalter; ein
 * eigener Wert ist hervorgehoben und lässt sich mit „Systemwert" zurücknehmen. Die
 * Eingabe bleibt Text, solange getippt wird (auch „18," ist ein Zwischenstand), und geht
 * bei jeder Änderung als „18.5mm" in den Entwurf.
 */
export function EinheitFeld(p: {
  id: string; label: string; einheit: "mm" | "pt" | "%"; wert: unknown; systemwert: string;
  fehler: string | undefined; onWert: (neu: string | undefined) => void;
  onVerlassen: () => void;
}): ReactElement {
  const eigen = p.wert !== undefined;
  const gesperrt = usePanelSperre();
  const [text, setText] = useState(zahlAus(p.wert, p.einheit));
  // Kommt der Wert von aussen (Verwerfen, Übernehmen, Systemwert), folgt das Feld.
  useEffect(() => {
    setText((alt) => (mitEinheit(alt, p.einheit) === p.wert ? alt : zahlAus(p.wert, p.einheit)));
  }, [p.wert, p.einheit]);
  return (
    <div className="lay__feld">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <div className="lay__eingabezeile">
        <LayEingabe id={p.id} name={p.label} wert={text} zahl schmal eigen={eigen}
          platzhalter={zahlAus(p.systemwert, p.einheit)} fehler={p.fehler}
          onText={(t) => { setText(t); p.onWert(mitEinheit(t, p.einheit)); }}
          onVerlassen={p.onVerlassen} />
        <span className="lay__einheit">{p.einheit}</span>
        {eigen && !gesperrt && (
          <SystemwertKnopf label={p.label}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }} />
        )}
      </div>
      <FeldBefund id={`${p.id}-fehler`} text={p.fehler} />
    </div>
  );
}

/** Text → Zahl für ZahlFeld: leer = undefined, Komma wird Punkt; was keine Zahl ist, geht
 *  als Text hinaus — der Server sagt am Feld, was erlaubt ist (wie EinheitFeld). */
export function zahlEingabe(text: string): number | string | undefined {
  const roh = text.trim().replace(",", ".");
  if (roh === "") return undefined;
  const n = Number(roh);
  return Number.isFinite(n) ? n : text.trim();
}

/**
 * Ein Zahlenfeld für Entwurfswerte, die als ZAHL im Layout stehen (Belegdesign Etappe 2:
 * `logo.prozent`, `logo.xMm`, `logo.yMm`) — dieselbe Familie wie EinheitFeld (Platzhalter,
 * Fehler am Feld, Kennung aus dem Pfad für den Sprung zum ersten Fehler), nur ohne Einheit
 * im Wert. `hinweis` steht gedämpft unter dem Feld (z. B. Warnung 68 a). `systemwertKnopf`
 * nimmt einen eigenen Wert zurück.
 */
export function ZahlFeld(p: {
  id: string; label: string; einheit: "mm" | "%"; wert: unknown; platzhalter: string;
  fehler: string | undefined; hinweis?: string | undefined; gesperrt?: boolean | undefined;
  systemwertKnopf?: boolean | undefined;
  onWert: (neu: number | string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const eigen = p.wert !== undefined;
  const alsText = (w: unknown): string =>
    typeof w === "number" || typeof w === "string" ? String(w) : "";
  const [text, setText] = useState(alsText(p.wert));
  // Kommt der Wert von aussen (Ziehen im Blatt, Verwerfen, „Wie bisher“), folgt das Feld;
  // ein Zwischenstand wie „18,“ bleibt stehen, solange er denselben Wert meint.
  useEffect(() => {
    setText((alt) => (zahlEingabe(alt) === p.wert ? alt : alsText(p.wert)));
  }, [p.wert]);
  const gesperrt = usePanelSperre() || p.gesperrt === true;
  return (
    <div className="lay__feld lay__feld--klein">
      <label className="lay__label" htmlFor={p.id}>{p.label}</label>
      <div className="lay__eingabezeile">
        <LayEingabe id={p.id} name={p.label} wert={text} zahl schmal eigen={eigen}
          platzhalter={p.platzhalter} fehler={p.fehler} hilfe={p.hinweis} gesperrt={gesperrt}
          onText={(t) => { setText(t); p.onWert(zahlEingabe(t)); }}
          onVerlassen={p.onVerlassen} />
        <span className="lay__einheit">{p.einheit}</span>
        {eigen && p.systemwertKnopf === true && !gesperrt && (
          <SystemwertKnopf label={p.label}
            onClick={() => { setText(""); p.onWert(undefined); p.onVerlassen(); }} />
        )}
      </div>
      <FeldBefund id={`${p.id}-fehler`} text={p.fehler} />
    </div>
  );
}

/** Eine Auswahl, deren leerer Wert „Systemwert" heisst. Eine Wahl ist eine fertige
 *  Eingabe — der Aufrufer fordert die Vorschau gleich an. */
export function WahlFeld(p: {
  id: string; label: string; wert: string; optionen: readonly { wert: string; text: string }[];
  fehler: string | undefined; onWahl: (wert: string) => void;
}): ReactElement {
  const gesperrt = usePanelSperre();
  return (
    <div className="lay__feld">
      <Auswahl id={p.id} beschriftung={p.label} wert={p.wert}
        className={`lay__me${p.wert !== "" ? " lay__eigen" : ""}`}
        {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
        {...(gesperrt ? { gesperrt: true } : {})}
        onGeaendert={(d) => { if (!gesperrt) p.onWahl(d.wert); }}>
        {/* Der Text gehört in den Schlüssel: me-auswahl liest seine Optionen bei `slotchange`
            — ein bloß geänderter Text käme sonst nicht im inneren `<select>` an. */}
        {p.optionen.map((o) => <option key={`${o.wert}|${o.text}`} value={o.wert}>{o.text}</option>)}
      </Auswahl>
      <FeldBefund id={`${p.id}-fehler`} text={p.fehler} />
    </div>
  );
}

/** Ein Schalter: zeigt den WIRKSAMEN Wert (eigen, sonst System); ein eigener Wert ist
 *  markiert und lässt sich zurücknehmen. */
export function SchalterFeld(p: {
  id: string; label: string; wert: unknown; systemwert: boolean; fehler: string | undefined;
  onWert: (neu: boolean | undefined) => void;
}): ReactElement {
  const eigen = typeof p.wert === "boolean";
  const an = eigen ? p.wert as boolean : p.systemwert;
  const gesperrt = usePanelSperre();
  return (
    <div className="lay__feld">
      <div className="lay__eingabezeile">
        <Schalter id={p.id} beschriftung={p.label} an={an}
          className={`lay__me${eigen ? " lay__eigen" : ""}`}
          {...(p.fehler === undefined ? {} : { fehler: p.fehler })}
          {...(gesperrt ? { gesperrt: true } : {})}
          onGeaendert={(d) => { if (!gesperrt) p.onWert(d.an); }} />
        {eigen && !gesperrt && (
          <SystemwertKnopf label={p.label} zusatz={`(${p.systemwert ? "an" : "aus"})`}
            onClick={() => p.onWert(undefined)} />
        )}
      </div>
      <FeldBefund id={`${p.id}-fehler`} text={p.fehler} />
    </div>
  );
}

/** Der Wortlaut der Markdown-Regel und des Sofort-Hinweises (Fixrunde 1, W1). */
const MARKDOWN_HILFE = "Markdown im Beleg-Tagsatz; kein HTML — die Zeichen < und > sind nicht erlaubt";
const SPITZE_KLAMMERN = "Die Zeichen < und > sind nicht erlaubt.";

/** Ein Text (einzeilig oder mehrzeilig). Leer heisst „Systemwert"; der steht als
 *  Platzhalter. `hinweis` sagt, woher ein Platzhalter stammt (Erbe der Korrektur).
 *
 *  Fixrunde 1 (W1), beides optional: `max` zeigt einen Zähler „n / max" (das Element warnt
 *  ab 95 %, darüber Fehlerfarbe); `markdown` nennt die Regel des Beleg-Tagsatzes unter dem
 *  Feld und weist `<`/`>` sofort am Feld ab, noch vor dem Rundlauf — maßgeblich bleibt der
 *  Server. */
export function TextFeld(p: {
  id: string; label: string; wert: unknown; systemwert: string; mehrzeilig: boolean;
  hinweis?: string | undefined; fehler: string | undefined;
  max?: number | undefined; markdown?: boolean | undefined;
  /** Falsch: kein Knopf „Systemwert“ (z. B. Fußspalten: leer ist keine Systemvorgabe). */
  systemwertKnopf?: boolean | undefined;
  onWert: (neu: string | undefined) => void; onVerlassen: () => void;
}): ReactElement {
  const eigen = typeof p.wert === "string";
  const text = eigen ? p.wert as string : "";
  const gesperrt = usePanelSperre();
  const klammern = p.markdown === true && /[<>]/.test(text);
  return (
    <div className="lay__feld">
      <LayEingabe id={p.id} beschriftung={p.label} wert={text} eigen={eigen}
        mehrzeilig={p.mehrzeilig} platzhalter={p.systemwert} max={p.max}
        hilfe={p.markdown === true ? MARKDOWN_HILFE : undefined}
        fehler={befundText(klammern ? SPITZE_KLAMMERN : undefined, p.fehler)}
        onText={(t) => p.onWert(t === "" ? undefined : t)} onVerlassen={p.onVerlassen} />
      {p.hinweis !== undefined && !eigen && <span className="lay__erbe">{p.hinweis}</span>}
      {eigen && p.systemwertKnopf !== false && !gesperrt && (
        <div className="lay__werkzeuge">
          <SystemwertKnopf label={p.label} onClick={() => { p.onWert(undefined); p.onVerlassen(); }} />
        </div>
      )}
      <FeldBefund id={`${p.id}-klammern`} text={klammern ? SPITZE_KLAMMERN : undefined} />
      <FeldBefund id={`${p.id}-fehler`} text={p.fehler} />
    </div>
  );
}
